import { BellRing } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { relancesAPI } from "../services/api";
import { formatRelanceDate } from "./relanceDates";
import RelanceEmailPreview from "./RelanceEmailPreview";

const RelanceSettings = ({ register, watch, getValues, errors }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const options = {
    relances_jours: watch("relances_jours") || [3, 10, 20],
    relances_avant_echeance: !!watch("relances_avant_echeance"),
    relances_intervalle_min: watch("relances_intervalle_min") || 3,
    smtp_host: watch("smtp_host") || "",
  };
  const active = watch("relances_auto");
  const signature = JSON.stringify(options);
  const currentSignature = useRef(signature);
  currentSignature.current = signature;
  useEffect(() => { setPreview(null); }, [signature]);

  const calculatePreview = async () => {
    setLoading(true);
    try {
      const response = await relancesAPI.preview(options);
      if (currentSignature.current === signature) setPreview(response.data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Vérifiez les délais de relance");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center">
        <BellRing className="h-5 w-5 mr-2 text-primary-600" /> Relances automatiques
      </h2>
      <p className="text-sm text-gray-600">
        Contrôle chaque jour à 9 h, heure de Paris, lorsque le serveur est actif.
        Les relances s'arrêtent dès que la facture est marquée payée ou annulée.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[0, 1, 2].map((index) => (
          <label key={index} className="block text-sm font-medium text-gray-700">
            Relance {index + 1}, jours après échéance
            <input type="number" min="1" max="365" step="1" className="input-field mt-1"
              {...register(`relances_jours.${index}`, {
                valueAsNumber: true, required: "Délai requis",
                min: { value: 1, message: "Minimum 1 jour" },
                max: { value: 365, message: "Maximum 365 jours" },
                validate: (value) => (Number.isInteger(value) &&
                  (index === 0 || value > Number(getValues(`relances_jours.${index - 1}`)))) ||
                  "Choisissez trois nombres entiers strictement croissants",
              })} />
            {errors.relances_jours?.[index] && (
              <span className="block text-red-600 mt-1">{errors.relances_jours[index].message}</span>
            )}
          </label>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" {...register("relances_avant_echeance")} />
        Ajouter un rappel courtois 3 jours avant l'échéance
      </label>
      <label className="block text-sm font-medium text-gray-700 max-w-sm">
        Espacement minimum entre deux emails (jours)
        <input type="number" min="1" max="30" step="1" className="input-field mt-1"
          {...register("relances_intervalle_min", {
            valueAsNumber: true, required: "Espacement requis",
            min: { value: 1, message: "Minimum 1 jour" },
            max: { value: 30, message: "Maximum 30 jours" },
            validate: (value) => Number.isInteger(value) || "Choisissez un nombre entier",
          })} />
        {errors.relances_intervalle_min && (
          <span className="block text-red-600 mt-1">{errors.relances_intervalle_min.message}</span>
        )}
      </label>
      <p className="text-sm text-gray-600">
        Un envoi manuel récent décale la prochaine relance. Pour une ancienne facture,
        seul le dernier palier dû est envoyé. Après la dernière relance, le tableau de bord
        signale le suivi à effectuer.
      </p>
      <button type="button" onClick={calculatePreview} disabled={loading} className="btn-secondary">
        {loading ? "Calcul de l'aperçu..." : "Voir les factures concernées"}
      </button>
      {preview && (
        <div className="space-y-2" aria-live="polite">
          <p className="text-sm font-medium text-gray-900">
            {preview.a_envoyer} facture(s) dont la relance est actuellement due avec ces réglages.
          </p>
          <p className="text-xs text-gray-500">Aperçu uniquement, aucun email envoyé.</p>
          {preview.factures.length === 0 ? <p className="text-sm text-gray-500">Aucune facture non payée.</p> : (
            <div className="overflow-auto max-h-80 rounded-lg border border-gray-200">
              <table className="min-w-full text-sm text-left">
                <thead className="bg-gray-50"><tr>
                  <th className="p-3">Facture / client</th><th className="p-3">Échéance</th><th className="p-3">Prochaine relance</th><th className="p-3">Mail</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-200">
                  {preview.factures.map((row) => <tr key={row.facture_id}>
                    <td className="p-3">{row.numero}<br /><span className="text-gray-500">{row.client_nom || "Client absent"}</span></td>
                    <td className="p-3 whitespace-nowrap">{formatRelanceDate(row.date_echeance)}</td>
                    <td className="p-3">{row.motif || (row.a_envoyer ? "Due, prochain contrôle" : formatRelanceDate(row.prochaine_relance, true))}</td>
                    <td className="p-3"><RelanceEmailPreview factureId={row.facture_id} numero={row.numero}
                      paliers={options.relances_jours} initialPalier={row.palier} /></td>
                  </tr>)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
        <input type="checkbox" disabled={!active && !preview} {...register("relances_auto")} />
        Activer les relances automatiques
      </label>
      <p className="text-xs text-gray-500">
        Consultez l'aperçu avant activation, puis sauvegardez les paramètres.
        La configuration SMTP doit être renseignée. Les factures anciennes nécessitent
        une confirmation de leur envoi initial dans le tableau de bord.
      </p>
    </div>
  );
};

export default RelanceSettings;
